/**
 * Inline formatting in the memo (DOP-641): bold, italic, underline and
 * strikethrough a person gives words.
 *
 * The memo stores it on each sentence as ranges of that sentence's text
 * (`format: [{start, end, style}]`), so the wording, and the source a
 * sentence was verified against, never change with it. Edit mode shows it
 * as the editor's marks; saving reads the marks back into ranges, one
 * sentence at a time.
 */

import type { JSONContent } from "@tiptap/react";

import type { DealScreeningFormatSpan, DealScreeningFormatStyle } from "../../types/dealScreening";

export const FORMAT_STYLES: DealScreeningFormatStyle[] = ["bold", "italic", "underline", "strike"];

/** Each style's mark in the editor; they share their names. */
const isStyle = (name: string | undefined): name is DealScreeningFormatStyle =>
	FORMAT_STYLES.includes(name as DealScreeningFormatStyle);

/** A run of text in one style: offsets into the text it was read from. */
export type FormatSpan = DealScreeningFormatSpan;

/**
 * Text as editor text nodes, each piece carrying the styles that cover it
 * and the marks every piece keeps (a source note).
 */
export function formattedNodes(
	text: string,
	format: FormatSpan[] | undefined,
	keep: NonNullable<JSONContent["marks"]> = [],
): JSONContent[] {
	if (!text) return [];
	const spans = (format ?? []).filter((f) => f.start < f.end && f.start < text.length);
	if (spans.length === 0) return [{ type: "text", text, ...(keep.length > 0 ? { marks: keep } : {}) }];
	const cuts = new Set([0, text.length]);
	spans.forEach((f) => {
		cuts.add(Math.max(0, f.start));
		cuts.add(Math.min(text.length, f.end));
	});
	const edges = Array.from(cuts).sort((a, b) => a - b);
	const nodes: JSONContent[] = [];
	for (let i = 0; i < edges.length - 1; i += 1) {
		const [from, to] = [edges[i], edges[i + 1]];
		const styles = FORMAT_STYLES.filter((style) =>
			spans.some((f) => f.style === style && f.start <= from && f.end >= to),
		);
		const marks = [...styles.map((type) => ({ type })), ...keep];
		nodes.push({ type: "text", text: text.slice(from, to), ...(marks.length > 0 ? { marks } : {}) });
	}
	return nodes;
}

/** Spans of one style that touch or overlap, joined. */
function merged(spans: FormatSpan[]): FormatSpan[] {
	const out: FormatSpan[] = [];
	for (const style of FORMAT_STYLES) {
		const own = spans.filter((s) => s.style === style).sort((a, b) => a.start - b.start);
		for (const span of own) {
			const last = out[out.length - 1];
			if (last && last.style === style && span.start <= last.end) last.end = Math.max(last.end, span.end);
			else out.push({ ...span });
		}
	}
	return out.sort((a, b) => a.start - b.start || a.end - b.end || a.style.localeCompare(b.style));
}

/**
 * Inline editor content as its plain text and the styled runs in it. A chip
 * carries no text; a line break is a new line.
 */
export function runsOf(content: JSONContent[]): { text: string; spans: FormatSpan[] } {
	let text = "";
	const spans: FormatSpan[] = [];
	const walk = (node: JSONContent) => {
		if (node.type === "text") {
			const piece = node.text ?? "";
			(node.marks ?? []).forEach((mark) => {
				if (isStyle(mark.type) && piece) spans.push({ start: text.length, end: text.length + piece.length, style: mark.type });
			});
			text += piece;
			return;
		}
		if (node.type === "hardBreak") {
			text += "\n";
			return;
		}
		(node.content ?? []).forEach(walk);
	};
	content.forEach(walk);
	return { text, spans: merged(spans) };
}

/** Spans moved `by` characters and cut to `[0, length)`. */
export function shifted(spans: FormatSpan[], by: number, length: number): FormatSpan[] {
	return spans
		.map((s) => ({ ...s, start: Math.max(0, s.start + by), end: Math.min(length, s.end + by) }))
		.filter((s) => s.start < s.end);
}

/** The text trimmed, with its spans kept on the same characters. */
export function trimmed(text: string, spans: FormatSpan[]): { text: string; spans: FormatSpan[] } {
	const start = text.length - text.trimStart().length;
	const out = text.trim();
	return { text: out, spans: shifted(spans, -start, out.length) };
}

/**
 * Spans over a paragraph's text as each sentence's own (DOP-641): the
 * sentences are found in the text in order, and a span is cut to each one
 * it covers. `sentence` is the sentence's index.
 */
export function sentenceFormats(
	text: string,
	spans: FormatSpan[],
	sentences: string[],
): { sentence: number; start: number; end: number; style: DealScreeningFormatStyle }[] {
	if (spans.length === 0) return [];
	const out: { sentence: number; start: number; end: number; style: DealScreeningFormatStyle }[] = [];
	let cursor = 0;
	sentences.forEach((sentence, index) => {
		const at = text.indexOf(sentence, cursor);
		if (at < 0) return;
		cursor = at + sentence.length;
		for (const span of spans) {
			const start = Math.max(span.start, at) - at;
			const end = Math.min(span.end, at + sentence.length) - at;
			if (start < end) out.push({ sentence: index, start, end, style: span.style });
		}
	});
	return out;
}

/** Spans as a comparable string. */
export const spansKey = (spans: FormatSpan[] | undefined): string =>
	(spans ?? []).map((s) => `${s.start}-${s.end}-${s.style}`).join(",");

/** Text cut where its styles change, each piece with the styles on it. */
export function formatPieces(
	text: string,
	format: FormatSpan[] | undefined,
	/** Where `text` starts inside the text `format` is over. */
	offset = 0,
): { text: string; styles: DealScreeningFormatStyle[] }[] {
	const spans = shifted(format ?? [], -offset, text.length);
	if (spans.length === 0) return text ? [{ text, styles: [] }] : [];
	const cuts = new Set([0, text.length]);
	spans.forEach((s) => {
		cuts.add(s.start);
		cuts.add(s.end);
	});
	const edges = Array.from(cuts).sort((a, b) => a - b);
	const out: { text: string; styles: DealScreeningFormatStyle[] }[] = [];
	for (let i = 0; i < edges.length - 1; i += 1) {
		const [from, to] = [edges[i], edges[i + 1]];
		out.push({
			text: text.slice(from, to),
			styles: FORMAT_STYLES.filter((style) => spans.some((s) => s.style === style && s.start <= from && s.end >= to)),
		});
	}
	return out;
}

// ------------------------------------------------------------------ offsets on the wire (codex r2)

/**
 * A stored range counts characters as the server does: one per Unicode code
 * point, so an emoji is one. The editor counts UTF-16 units, where an emoji
 * is two. Ranges are turned into code points on the way out and back into
 * UTF-16 on the way in, so formatting after an emoji lands on the same words
 * in the editor, the saved memo and the exports.
 */
const codePointAt = (text: string, utf16: number): number => Array.from(text.slice(0, utf16)).length;
const utf16At = (text: string, codePoint: number): number =>
	Array.from(text).slice(0, codePoint).join("").length;

/** Editor ranges over `text` as the server stores them. */
export function toStoredOffsets<T extends { start: number; end: number }>(text: string, spans: T[]): T[] {
	return spans.map((s) => ({ ...s, start: codePointAt(text, s.start), end: codePointAt(text, s.end) }));
}

/** Stored ranges over `text` as the editor and the page count them. */
export function fromStoredOffsets<T extends { start: number; end: number }>(text: string, spans: T[] | undefined): T[] {
	return (spans ?? []).map((s) => ({ ...s, start: utf16At(text, s.start), end: utf16At(text, s.end) }));
}
